import { create } from 'zustand';
// Must come before anything that reads the registry: this store builds its default
// technique selection at module-evaluation time, and without an explicit import the
// order would depend on which file happened to be imported first.
import '../techniques/register';
import type { StudioDocument } from './studioStore';
import { CURATED_PALETTES } from '../core/palette';
import { POST_EFFECTS } from '../core/postfx/chain';
import { randomSeed } from '../core/rng';
import { listTechniques } from '../techniques/registry';
import { BatchEngine } from '../bulk/engine';
import { createVariant, planBatch, type Plan } from '../bulk/plan';
import type { BatchConfig, BatchProgress, BatchStatus, PostMode, Variant } from '../bulk/types';

function defaultConfig(): BatchConfig {
  const techniques = listTechniques().map((t) => t.id);
  const postMode: Record<string, PostMode> = {};
  for (const effect of POST_EFFECTS) postMode[effect.id] = 'off';

  return {
    strategy: 'random',
    count: 24,

    varyText: false,
    words: ['FORM', 'FELD', 'WACHSTUM', 'RAUSCHEN'],
    varyFonts: false,
    fontIds: ['anton', 'playfair-display', 'space-grotesk', 'unifraktur-maguntia'],
    varyCanvas: false,
    canvasPresetIds: ['square-1080', 'portrait-1080'],
    varyPalettes: true,
    paletteIds: CURATED_PALETTES.map((p) => p.id),

    techniqueIds: techniques,
    techniqueWeights: Object.fromEntries(techniques.map((id) => [id, 1])),
    combineLayers: false,
    minLayers: 1,
    maxLayers: 2,

    varyBackground: false,
    backgroundKinds: ['solid', 'linear', 'radial'],

    postMode,
    postProbability: 0.4,

    seedFrom: 1,
    seedTo: 1000,

    includeTextParams: false,
    includeLayerParams: true,
    includeBackgroundParams: false,
    includePostParams: false,

    gridAxes: [],

    mutationStrength: 0.3,
    mutationBaseId: null,
    morphTargetPresetId: null,

    previewSize: 420,
    exportScale: 2,
    format: 'webp',
    quality: 0.85,
    stepRange: [1, 1],
    workers: Math.max(2, Math.min(8, (navigator.hardwareConcurrency || 4) - 1)),
  };
}

export type GalleryFilter = 'all' | 'favourites' | 'failed';
export type GallerySort = 'order' | 'random' | 'speed';

export interface BatchState {
  config: BatchConfig;
  variants: Variant[];
  progress: BatchProgress;
  plan: Plan | null;
  planSeed: number;

  // Gallery view state
  thumbSize: number;
  filter: GalleryFilter;
  techniqueFilter: string | null;
  sort: GallerySort;
  selection: string[];
  detailId: string | null;
  compareIds: string[];

  setConfig: (patch: Partial<BatchConfig>) => void;
  setPostMode: (effectId: string, mode: PostMode) => void;
  setTechniqueWeight: (id: string, weight: number) => void;
  rollPlanSeed: () => void;
  preview: (doc: StudioDocument, mutationBase?: StudioDocument, morphTarget?: StudioDocument) => Plan;

  run: (doc: StudioDocument, mutationBase?: StudioDocument, morphTarget?: StudioDocument) => void;
  pause: () => void;
  resume: () => void;
  cancel: () => void;
  clear: () => void;

  toggleFavourite: (id: string) => void;
  toggleHidden: (id: string) => void;
  setThumbSize: (n: number) => void;
  setFilter: (f: GalleryFilter) => void;
  setTechniqueFilter: (id: string | null) => void;
  setSort: (s: GallerySort) => void;
  select: (id: string, additive: boolean) => void;
  clearSelection: () => void;
  setDetail: (id: string | null) => void;
  toggleCompare: (id: string) => void;
}

const idleProgress = (): BatchProgress => ({
  status: 'idle',
  total: 0,
  completed: 0,
  failed: 0,
  elapsed: 0,
  remaining: null,
  currentTechnique: '',
});

let engine: BatchEngine | null = null;

export const useBatch = create<BatchState>((set, get) => {
  const ensureEngine = (): BatchEngine => {
    if (engine) return engine;
    engine = new BatchEngine({
      onVariant: (variant) => {
        set((s) => ({
          variants: s.variants.map((v) => (v.id === variant.id ? { ...variant } : v)),
        }));
      },
      onProgress: (progress) => set({ progress }),
      onFinished: (status: BatchStatus) => set((s) => ({ progress: { ...s.progress, status } })),
    });
    return engine;
  };

  const buildPlan = (doc: StudioDocument, mutationBase?: StudioDocument, morphTarget?: StudioDocument): Plan =>
    planBatch(get().config, doc, get().planSeed, { mutationBase, morphTarget });

  return {
    config: defaultConfig(),
    variants: [],
    progress: idleProgress(),
    plan: null,
    planSeed: randomSeed(),

    thumbSize: 200,
    filter: 'all',
    techniqueFilter: null,
    sort: 'order',
    selection: [],
    detailId: null,
    compareIds: [],

    setConfig: (patch) => set((s) => ({ config: { ...s.config, ...patch } })),
    setPostMode: (effectId, mode) =>
      set((s) => ({ config: { ...s.config, postMode: { ...s.config.postMode, [effectId]: mode } } })),
    setTechniqueWeight: (id, weight) =>
      set((s) => ({ config: { ...s.config, techniqueWeights: { ...s.config.techniqueWeights, [id]: weight } } })),
    rollPlanSeed: () => set({ planSeed: randomSeed() }),

    preview: (doc, mutationBase, morphTarget) => {
      const plan = buildPlan(doc, mutationBase, morphTarget);
      set({ plan });
      return plan;
    },

    run: (doc, mutationBase, morphTarget) => {
      const plan = buildPlan(doc, mutationBase, morphTarget);
      const variants = plan.variants.map(createVariant);
      // Release the previous run's object URLs before dropping the references, or the
      // blobs stay alive for the lifetime of the page.
      for (const v of get().variants) if (v.url) URL.revokeObjectURL(v.url);

      set({ plan, variants, selection: [], detailId: null, compareIds: [] });

      const { config } = get();
      // Thumbnails are rendered at a scale derived from the longest edge, so a batch of
      // mixed canvas formats still produces comparable preview sizes.
      const requests = variants.map((variant) => {
        const longest = Math.max(variant.doc.canvas.width, variant.doc.canvas.height);
        return {
          variant,
          scale: Math.min(1, config.previewSize / longest),
          format: config.format,
          quality: config.quality,
          maxSteps: 40000,
        };
      });
      ensureEngine().start(requests, config.workers);
    },

    pause: () => engine?.pause(),
    resume: () => engine?.resume(),
    cancel: () => engine?.cancel(),
    clear: () => {
      engine?.cancel();
      for (const v of get().variants) if (v.url) URL.revokeObjectURL(v.url);
      set({ variants: [], plan: null, progress: idleProgress(), selection: [], detailId: null, compareIds: [] });
    },

    toggleFavourite: (id) =>
      set((s) => ({ variants: s.variants.map((v) => (v.id === id ? { ...v, favourite: !v.favourite } : v)) })),
    toggleHidden: (id) =>
      set((s) => ({ variants: s.variants.map((v) => (v.id === id ? { ...v, hidden: !v.hidden } : v)) })),
    setThumbSize: (thumbSize) => set({ thumbSize }),
    setFilter: (filter) => set({ filter }),
    setTechniqueFilter: (techniqueFilter) => set({ techniqueFilter }),
    setSort: (sort) => set({ sort }),
    select: (id, additive) =>
      set((s) => {
        if (!additive) return { selection: [id] };
        return { selection: s.selection.includes(id) ? s.selection.filter((x) => x !== id) : [...s.selection, id] };
      }),
    clearSelection: () => set({ selection: [] }),
    setDetail: (detailId) => set({ detailId }),
    toggleCompare: (id) =>
      set((s) => {
        if (s.compareIds.includes(id)) return { compareIds: s.compareIds.filter((x) => x !== id) };
        // Four panes is the point where the diff table stops being readable.
        return { compareIds: s.compareIds.length >= 4 ? s.compareIds : [...s.compareIds, id] };
      }),
  };
});
